import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@ab-platform/ui/dialog';
import { PillButton } from '@ab-platform/ui/pill-button';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { Banner } from '@ab-platform/ui/banner';
import { env } from '@/lib/env';
import {
  lookupCompany,
  type CompanyLookupCompany,
  type CompanyLookupResult,
} from '@/lib/api';
import { COMPANY_LOOKUP_UNAVAILABLE_LABELS } from '@/lib/company-lookup-prefill';
import { useWizardSessionIdContext } from '@/hooks/WizardSessionIdContext';

interface CompanyLookupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill the search input with the user's current P.IVA / CF. */
  defaultVatOrFiscalCode?: string;
  /** Called when the operator confirms "Usa questi dati". */
  onApply: (company: CompanyLookupCompany) => void;
}

const VAT_OR_FC_REGEX = /^[A-Z0-9]{11,16}$/;

function normalizeCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Recupera dati azienda — modal sopra lo step `/organizzazione` ramo
 * COMPANY. Chiama il BE company-lookup con un Turnstile token coppia
 * con la query, mostra il preview dei campi che verranno scritti e
 * scrive a form solo dopo che l'operatore conferma "Usa questi dati".
 *
 * Stati visibili (mutex):
 *   idle        → input + Turnstile + "Cerca"
 *   loading     → spinner sul "Cerca"
 *   found       → preview card + "Usa questi dati" / "Annulla"
 *   not_found   → soft banner + "Riprova" / "Chiudi"
 *   unavailable → soft banner (provider down/timeout/sandbox no-data) +
 *                 "Riprova" / "Chiudi". Il wizard manuale resta
 *                 utilizzabile quando si chiude la dialog.
 *   error       → 4xx/network — banner "Riprova" / "Chiudi".
 *
 * `Turnstile` viene rimontato a ogni ricerca per generare un token
 * fresco — Cloudflare invalida il token dopo l'uso lato server.
 */
export function CompanyLookupDialog({
  open,
  onOpenChange,
  defaultVatOrFiscalCode,
  onApply,
}: CompanyLookupDialogProps): JSX.Element {
  const wizardSessionId = useWizardSessionIdContext();
  const [code, setCode] = useState<string>(defaultVatOrFiscalCode ?? '');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [turnstileNonce, setTurnstileNonce] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<CompanyLookupResult | null>(null);

  const normalizedCode = normalizeCode(code);
  const isCodeValid = VAT_OR_FC_REGEX.test(normalizedCode);
  const canSearch = isCodeValid && turnstileToken.length > 0 && !isLoading;

  const reset = (): void => {
    setCode(defaultVatOrFiscalCode ?? '');
    setTurnstileToken('');
    setIsLoading(false);
    setResult(null);
    setTurnstileNonce((n) => n + 1);
  };

  const handleOpenChange = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSearch = async (): Promise<void> => {
    if (!canSearch) return;
    setIsLoading(true);
    setResult(null);
    try {
      const r = await lookupCompany(
        normalizedCode,
        turnstileToken,
        wizardSessionId || undefined,
      );
      setResult(r);
    } finally {
      setIsLoading(false);
      // Turnstile tokens are single-use. Force a fresh widget for any
      // follow-up search by bumping the key.
      setTurnstileNonce((n) => n + 1);
      setTurnstileToken('');
    }
  };

  const handleApply = (): void => {
    if (result?.kind === 'found') {
      onApply(result.company);
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Recupera dati azienda</DialogTitle>
          <DialogDescription>
            Inserisci P.IVA o codice fiscale aziendale: precompiliamo i campi
            disponibili. Puoi sempre modificarli prima di proseguire.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <FloatingLabelInput
            label="P.IVA o codice fiscale"
            autoComplete="off"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="[&_input]:uppercase"
            disabled={isLoading}
          />

          <Turnstile
            key={turnstileNonce}
            siteKey={env.VITE_TURNSTILE_SITE_KEY}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken('')}
            onExpire={() => setTurnstileToken('')}
            options={{ theme: 'light' }}
          />

          {result?.kind === 'found' ? (
            <FoundPreview company={result.company} />
          ) : null}

          {result?.kind === 'not_found' ? (
            <Banner tone="info">
              {COMPANY_LOOKUP_UNAVAILABLE_LABELS['NOT_FOUND']}
            </Banner>
          ) : null}

          {result?.kind === 'unavailable' ? (
            <Banner tone="warning">
              {COMPANY_LOOKUP_UNAVAILABLE_LABELS[result.reason] ??
                COMPANY_LOOKUP_UNAVAILABLE_LABELS['PROVIDER_UNAVAILABLE']}
            </Banner>
          ) : null}

          {result?.kind === 'error' ? (
            <Banner tone="warning">
              {result.code === 'TURNSTILE_INVALID'
                ? 'Verifica anti-abuso non riuscita. Riprova.'
                : result.code === 'RATE_LIMITED'
                  ? 'Troppe richieste. Aspetta qualche minuto e riprova.'
                  : 'Si è verificato un errore. Riprova o compila manualmente.'}
            </Banner>
          ) : null}
        </div>

        <DialogFooter>
          {result?.kind === 'found' ? (
            <>
              <PillButton
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
              >
                Annulla
              </PillButton>
              <PillButton type="button" onClick={handleApply}>
                Usa questi dati
              </PillButton>
            </>
          ) : (
            <>
              <PillButton
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
              >
                Chiudi
              </PillButton>
              <PillButton
                type="button"
                onClick={() => void handleSearch()}
                disabled={!canSearch}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Search className="h-4 w-4" aria-hidden />
                )}
                {isLoading ? 'Ricerca…' : 'Cerca'}
              </PillButton>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FoundPreview({
  company,
}: {
  company: CompanyLookupCompany;
}): JSX.Element {
  const items: { label: string; value: string }[] = [];
  if (company.legalName) items.push({ label: 'Ragione sociale', value: company.legalName });
  if (company.vatNumber) items.push({ label: 'P.IVA', value: company.vatNumber });
  if (company.fiscalCode) items.push({ label: 'Codice fiscale', value: company.fiscalCode });
  if (company.businessLegalForm) {
    items.push({ label: 'Tipo impresa', value: company.businessLegalForm });
  } else if (company.providerLegalFormLabel) {
    // The provider returned a legal form the BE could not map to the
    // closed list. We surface it as info but the FE setValue helper
    // does NOT write it (would corrupt the SelectField); the operator
    // picks manually.
    items.push({
      label: 'Forma giuridica (da selezionare)',
      value: company.providerLegalFormLabel,
    });
  }
  if (company.pecEmail) items.push({ label: 'PEC', value: company.pecEmail });
  if (company.sdiCode) items.push({ label: 'Codice SDI', value: company.sdiCode });

  const addr = company.legalAddress;
  const addressLine = addr
    ? [
        [addr.street, addr.streetNumber].filter(Boolean).join(' '),
        addr.zip,
        [addr.city, addr.province].filter(Boolean).join(' '),
        addr.country,
      ]
        .filter((v) => v && v.length > 0)
        .join(', ')
    : '';
  if (addressLine.length > 0) {
    items.push({ label: 'Sede legale', value: addressLine });
  }

  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-app-border bg-app-surface p-4">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        Dati trovati — verranno precompilati nel form
      </span>
      <ul className="flex flex-col gap-1.5">
        {items.length === 0 ? (
          <li className="text-sm text-muted-foreground">
            Il provider ha trovato l'azienda ma non ha restituito campi
            precompilabili.
          </li>
        ) : (
          items.map((it) => (
            <li
              key={it.label}
              className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3"
            >
              <span className="text-xs uppercase tracking-wide text-muted-foreground sm:w-44">
                {it.label}
              </span>
              <span className="text-sm text-foreground">{it.value}</span>
            </li>
          ))
        )}
      </ul>
      {company.activityStatus === 'CEASED' ? (
        <Banner tone="warning">
          Stato attività: cessata. Verifica i dati prima di usarli.
        </Banner>
      ) : null}
    </div>
  );
}
